// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Button, Divider, Group, Paper, Stack, Text } from '@mantine/core';
import { IconBallpen, IconCheck, IconTrash } from '@tabler/icons';
import { FC, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { i18n } from '@lingui/core';
import { Planning, PlanningGroup } from '../../Types/ResourceT';
import ResourcePlanningS from '../../Service/restapi/resourcePlanningS';
import ResourcePlanningTable from '../../Organismns/Resources/table/planning/ResourcePlanningTable';
import BookitupAddIcon from '../../Atoms/BookitupAddIcon';
import { WindowContext } from '../../Service/Context/WindowContext';
import { EventResourcePlanningContext } from '../../Service/Context/EventResourcePlanningContext';
import { openAddResourcePlanningModal } from './AddEventResourcePlanningModal';
import { openPlanningGroupModal, openDeletePlanningGroupModal } from './EventPlanningsModals';
import PanePaper from '../../Molecules/PanePaper';

interface Props {
  group: PlanningGroup;
}

const hasUnconfirmedPlanning = (plannings: Planning[]): boolean =>
  plannings.some((planning) => !planning.warehousePlanning || hasUnconfirmedPlanning(planning.children ?? []));

const EventPlanningGroup: FC<Props> = ({ group }) => {
  const { id, description, begin, end } = group;
  const [loading, setLoading] = useState(false);
  const [plannings, setPlannings] = useState<Planning[]>([]);
  const { sm } = useContext(WindowContext);
  const { editGroup, deleteGroup } = useContext(EventResourcePlanningContext);

  const fetchPlannings = () => {
    setLoading(true);
    ResourcePlanningS.fetchPlannings(id)
      .then(setPlannings)
      .finally(() => setLoading(false));
  };

  const onDelete = async (groupId: string) => {
    const deleted = await ResourcePlanningS.deletePlanningGroup(groupId);
    if (deleted) {
      deleteGroup(groupId);
    }
  };

  useEffect(() => {
    fetchPlannings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onConfirmAll = async () => {
    const confirmed = await ResourcePlanningS.confirmResourcePlannings(id);
    if (confirmed) {
      fetchPlannings();
    }
  };

  const hasUnconfirmedPlannings = hasUnconfirmedPlanning(plannings);
  const addResource = () => openAddResourcePlanningModal({ group, refresh: fetchPlannings });
  const actions = <BookitupAddIcon onClick={addResource} />;

  return (
    <PanePaper withoutBorder withShadow>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: sm ? 'stretch' : 'flex-start',
          gap: 12,
          flexDirection: sm ? 'column' : 'row',
        }}
      >
        <Stack spacing={4} sx={{ flex: 1 }}>
          <Group position="apart" align="flex-start" noWrap={!sm}>
            <Text weight={600}>{description}</Text>
            {sm && actions}
          </Group>
          <Text color="dimmed" size="xs" weight="lighter">{`${moment(begin).format('DD.MM.YYYY HH:mm')} - ${moment(
            end,
          ).format('DD.MM.YYYY HH:mm')}`}</Text>
          <Group my="sm" spacing={0}>
            {hasUnconfirmedPlannings && (
              <Button variant="subtle" leftIcon={<IconCheck size={14} />} size="xs" onClick={onConfirmAll}>
                Alle Planungen bestätigen
              </Button>
            )}
            <Button
              size="xs"
              variant="subtle"
              leftIcon={<IconBallpen size={16} />}
              onClick={() => openPlanningGroupModal({ initial: group, onFinish: editGroup, dates: [] })}
            >
              {i18n._('actions.edit')}
            </Button>
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftIcon={<IconTrash size={16} />}
              onClick={() => openDeletePlanningGroupModal({ group, onDelete: () => onDelete(group.id) })}
            >
              Löschen
            </Button>
          </Group>
        </Stack>
        {!sm && actions}
      </Box>
      <Box>
        {!loading && plannings.length === 0 && (
          <Text italic mt="xl" color="dimmed" size="xs" weight="lighter">
            Noch nichts hinzugefügt.
          </Text>
        )}

        {!loading && plannings.length > 0 && <ResourcePlanningTable plannings={plannings} refresh={fetchPlannings} />}
      </Box>
    </PanePaper>
  );
};

export default EventPlanningGroup;
